import React, { useState, useCallback } from "react";
import { ROLE, UPLOAD_CONTENT_TYPE, CONTENT_PRICING } from "../../../shared/constants/common";
import { isImage, isVideo } from "../../../shared/utils/function";
import UserTag from "../UserTag";
import FilePreviewMedia from "./FilePreviewMedia";

const getFileTypeLabel = (file, index) => {
  if (isImage(file)) return `Image ${index + 1}`;
  if (isVideo(file)) return `Video ${index + 1}`;
  return `File ${index + 1}`;
};

const getPriceDisplay = (price, canCharge) => {
  if (!canCharge) return "$0";
  return price ? `$${price}` : "$0";
};

const isPaidContent = (selectedOption) => selectedOption === UPLOAD_CONTENT_TYPE.PAID;

/**
 * ContentCaptionSection Component
 * 
 * Handles captions, pricing, and tagging for uploaded content files and bundles
 * 
 * @param {Array} allFiles - Array of uploaded files with captions
 * @param {Array} bundleFiles - Array of bundle files
 * @param {string} selectedOption - UPLOAD_CONTENT_TYPE.PAID or UPLOAD_CONTENT_TYPE.FREE
 * @param {boolean} canCharge - Whether user can charge for content
 * @param {string} userRole - User role (CREATOR_CONTENT, CREATOR_SUB, etc)
 * @param {Object} filePrices - Object mapping file indices to prices
 * @param {Object} priceInputs - Object mapping file indices to price input values
 * @param {string} bundleCaption - Caption for the bundle
 * @param {string} bundlePrice - Price for the bundle
 * @param {string} bundlePriceInput - Input value for bundle price
 * @param {Object} selectedTaggedUsersByFile - Object mapping file indices to tagged users
 * @param {Array} selectedTaggedUsersBundle - Array of tagged users for bundle
 * @param {Function} onCaptionChange - Handler for caption changes
 * @param {Function} onFileRemove - Handler for file removal
 * @param {Function} onPriceInputChange - Handler for price input changes
 * @param {Function} onSetPrice - Handler for setting/unsetting prices
 * @param {Function} onBundleCaptionChange - Handler for bundle caption changes
 * @param {Function} onBundlePriceInputChange - Handler for bundle price input changes
 * @param {Function} onBundleSetPrice - Handler for setting/unsetting bundle price
 * @param {Function} onTaggedUsersChange - Handler for tagged users changes
 * @param {Function} onBundleTaggedUsersChange - Handler for bundle tagged users changes
 * @param {boolean} priceValidationAttempted - Whether user attempted upload without setting prices (paid content only)
 * @param {boolean} forCreatorMessaging - When true, tag UI is hidden (e.g. creator messaging flow)
 */
const ContentCaptionSection = ({
  allFiles,
  bundleFiles,
  selectedOption,
  canCharge,
  userRole,
  filePrices,
  priceInputs,
  bundleCaption,
  bundlePrice,
  bundlePriceInput,
  selectedTaggedUsersByFile,
  selectedTaggedUsersBundle,
  onCaptionChange,
  onFileRemove,
  onPriceInputChange,
  onSetPrice,
  onBundleCaptionChange,
  onBundlePriceInputChange,
  onBundleSetPrice,
  onTaggedUsersChange,
  onBundleTaggedUsersChange,
  priceValidationAttempted = false,
  forCreatorMessaging = false,
}) => {
  // Local state for tag popup
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [tagPopupTarget, setTagPopupTarget] = useState(null);

  // Helper to check if price badge should show
  const shouldShowPriceBadge = useCallback((selectedOption, userRole) => {
    return !(selectedOption === UPLOAD_CONTENT_TYPE.FREE || userRole === ROLE.CREATOR_SUB);
  }, []);

  // Helper to check if price input should show
  const shouldShowPriceInput = useCallback(
    (selectedOption, canCharge, userRole, forCreatorMessaging) => {
      // Allow standard creator content accounts to set prices for paid content
      if (selectedOption === UPLOAD_CONTENT_TYPE.PAID && canCharge && userRole === ROLE.CREATOR_CONTENT) {
        return true;
      }

      // In creator messaging, allow monthly creators to set prices for paid content
      if (
        selectedOption === UPLOAD_CONTENT_TYPE.PAID &&
        canCharge &&
        forCreatorMessaging &&
        userRole === ROLE.CREATOR_SUB
      ) {
        return true;
      }

      return false;
    },
    []
  );

  // Tag popup handlers
  const handleOpenTagPopupForBundle = useCallback(() => {
    setShowTagPopup(true);
    setTagPopupTarget({ type: 'bundle' });
  }, []);

  const handleOpenTagPopupForFile = useCallback((index) => {
    setShowTagPopup(true);
    setTagPopupTarget({ type: 'file', index });
  }, []);

  const handleTagPopupClose = useCallback(() => {
    setShowTagPopup(false);
    setTagPopupTarget(null);
  }, []);

  const handleTagPopupConfirm = useCallback((selectedUsers) => {
    if (!tagPopupTarget) {
      return;
    }

    if (tagPopupTarget.type === 'bundle') {
      onBundleTaggedUsersChange(selectedUsers);
    } else if (tagPopupTarget.type === 'file') {
      onTaggedUsersChange(tagPopupTarget.index, selectedUsers);
    }
    handleTagPopupClose();
  }, [tagPopupTarget, onBundleTaggedUsersChange, onTaggedUsersChange, handleTagPopupClose]);

  // Remove tagged user from bundle
  const handleRemoveBundleTaggedUser = useCallback((username) => {
    if (!Array.isArray(selectedTaggedUsersBundle)) {
      return;
    }
    const updatedUsers = selectedTaggedUsersBundle.filter(u => u && u.username !== username);
    onBundleTaggedUsersChange(updatedUsers);
  }, [selectedTaggedUsersBundle, onBundleTaggedUsersChange]);

  // Remove tagged user from file
  const handleRemoveFileTaggedUser = useCallback((fileIndex, username) => {
    const currentTags = selectedTaggedUsersByFile[fileIndex] || [];
    if (!Array.isArray(currentTags)) {
      return;
    }
    const updatedUsers = currentTags.filter(u => u && u.username !== username);
    onTaggedUsersChange(fileIndex, updatedUsers);
  }, [selectedTaggedUsersByFile, onTaggedUsersChange]);

  // Don't render if no files
  if (allFiles.length === 0 && bundleFiles.length === 0) {
    return null;
  }

  return (
    <>
      <div className="add-captions-section">
        <h3 className="add-captions-title mb-3">Add Captions</h3>
        {selectedOption === UPLOAD_CONTENT_TYPE.PAID && userRole !== ROLE.CREATOR_SUB && (
          <p className="cm_add_captions_subtitle">You keep 100% of what you charge.</p>
        )}

        {/* Bundle Pricing & Caption Section */}
        {bundleFiles.length > 0 && (
          <div className="bundle-pricing-container">
            <h4 className="text-white text-sm">
              {shouldShowPriceInput(selectedOption, canCharge, userRole, forCreatorMessaging)
                ? "Bundle Pricing & Caption"
                : "Bundle Caption"}
            </h4>

            <textarea
              className="bundle-caption-textarea"
              rows="3"
              placeholder="Add a caption for this bundle..."
              value={bundleCaption}
              onChange={onBundleCaptionChange}
            />

            {shouldShowPriceInput(selectedOption, canCharge, userRole, forCreatorMessaging) && (
              <>
                <div className="caption-price-section mb-3">
                  <div className="caption-price-row">
                    <div className="cm_caption_price_section">
                      <span className="cm_doller_icon">
                        $
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]+"
                        className="bundle-price-input price-input-dollar-padding"
                        // placeholder={`${CONTENT_PRICING.MIN_PRICE.toFixed(2)} minimum`}
                        placeholder={`Charge up to $${CONTENT_PRICING.MAX_PRICE}`}
                        min={CONTENT_PRICING.MIN_PRICE.toFixed(2)}
                        value={bundlePriceInput}
                        onChange={onBundlePriceInputChange}
                        readOnly={!!(bundlePrice && bundlePrice !== "" && bundlePrice !== "0")}
                        aria-label={bundlePrice ? "Bundle price set; unset to change" : "Bundle price"}
                      />
                    </div>
                    <button
                      type="button"
                      className={`bundle-set-button ${bundlePrice && bundlePrice !== "" && bundlePrice !== "0" ? "bundle-set-button--remove" : ""}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onBundleSetPrice();
                      }}
                    >
                      {bundlePrice && bundlePrice !== "" && bundlePrice !== "0" ? "Remove" : "Set"}
                    </button>
                  </div>
                  {/* {bundlePrice && bundlePrice !== "" && bundlePrice !== "0" ? (
                    <p className="text-xs text-[#737373] mt-1">Unset the price to change it.</p>
                  ) : null} */}
                  {priceValidationAttempted && (!bundlePrice || bundlePrice === "" || bundlePrice === "0") && (
                    <p className="price-field-error">No price has been set</p>
                  )}
                </div>
                {/* <p className="text-xs text-[#737373] mb-3">You keep 100% of what you charge.</p> */}
              </>
            )}

            {!forCreatorMessaging && (
              <div className="bundle-tag-section-wrapper">
                {selectedTaggedUsersBundle.length > 0 ? (
                  <>
                    {selectedTaggedUsersBundle.map((user) => (
                      <span key={user.username || user._id || user.id} className="tagged-user-chip">
                        @{user.username}
                        <button
                          className="tagged-user-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveBundleTaggedUser(user.username);
                          }}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </span>
                    ))}
                    <button
                      className="bundle-add-tag-button"
                      onClick={handleOpenTagPopupForBundle}
                    >
                      <i className="fa fa-plus" />
                    </button>
                  </>
                ) : (
                  <button
                    className="bundle-tag-button"
                    onClick={handleOpenTagPopupForBundle}
                  >
                    Tag
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Individual File Captions */}
        <div className="caption-items">
          {allFiles.map((fileObj, index) => (
            <div
              key={`caption-input-${index}-${selectedOption}`}
              className="caption-item"
            >
              <div className="caption-item-content">
                {/* File Thumbnail */}
                <div className="caption-item-thumbnail">
                  <FilePreviewMedia
                    fileObj={fileObj}
                    variant="compact"
                    imageClassName=""
                    placeholderIconClassName="file-placeholder-icon-white"
                    showPlaceholderName={false}
                  />
                </div>

                {/* File Details */}
                <div className="caption-item-details">
                  <div className="caption-item-header">
                    <div className="caption-item-info">
                      {isPaidContent(selectedOption) ? (
                        <i className="fa fa-lock caption-item-icon" aria-hidden="true" />
                      ) : (
                        <i className="fa fa-unlock caption-item-icon" aria-hidden="true" />
                      )}
                      <span className="caption-item-name">
                        {getFileTypeLabel(fileObj.file, index)}
                      </span>
                      {shouldShowPriceBadge(selectedOption, userRole) && (
                        // ? "bg-[#f534bf] bg-opacity-50"
                        // : "bg-[#49de80]"
                        <div className={`caption-item-type-badge ${selectedOption === UPLOAD_CONTENT_TYPE.FREE
                          ? "badge-pink"
                          : "badge-green"
                          }`}>
                          {selectedOption === UPLOAD_CONTENT_TYPE.FREE
                            ? "Free"
                            : getPriceDisplay(filePrices[index], canCharge)}
                        </div>
                      )}
                    </div>
                    <button
                      className="caption-item-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFileRemove(index);
                      }}
                    >
                      <i className="fa fa-xmark" />
                    </button>
                  </div>

                  {/* Caption Textarea */}
                  <textarea
                    className="caption-textarea"
                    rows="2"
                    value={fileObj.caption}
                    onChange={(e) => onCaptionChange(e, index)}
                    placeholder={`Add a caption for this ${isImage(fileObj.file) ? "image" : "video"}...`}
                  />

                  {/* Price Input */}
                  {shouldShowPriceInput(selectedOption, canCharge, userRole, forCreatorMessaging) && (
                    <div className="caption-price-section">
                      <div className="caption-price-row">
                        <div className="cm_caption_price_section">
                          <span className="cm_doller_icon">$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*[.,]?[0-9]+"
                            className="caption-price-input price-input-dollar-padding"
                            // placeholder={`${CONTENT_PRICING.MIN_PRICE.toFixed(2)} minimum`}
                            placeholder={`Charge up to $${CONTENT_PRICING.MAX_PRICE}`}
                            min={CONTENT_PRICING.MIN_PRICE.toFixed(2)}
                            value={priceInputs[index] || ""}
                            onChange={(e) => onPriceInputChange(index, e.target.value)}
                            readOnly={!!(filePrices[index] && filePrices[index] !== "" && filePrices[index] !== "0")}
                            aria-label={filePrices[index] ? "Price set; unset to change" : "Price"}
                          />
                        </div>
                        <button
                          type="button"
                          className={`caption-set-button ${filePrices[index] && filePrices[index] !== "" && filePrices[index] !== "0" ? "caption-set-button--remove" : ""}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSetPrice(index);
                          }}
                        >
                          {filePrices[index] && filePrices[index] !== "" && filePrices[index] !== "0"
                            ? "Remove"
                            : "Set"}
                        </button>
                      </div>
                      {/* {filePrices[index] && filePrices[index] !== "" && filePrices[index] !== "0" ? (
                        <p className="text-xs text-[#737373] mt-1">Unset the price to change it.</p>
                      ) : null} */}
                      {priceValidationAttempted && (!filePrices[index] || filePrices[index] === "" || filePrices[index] === "0") && (
                        <div className="price-field-error">No price has been set</div>
                      )}
                    </div>
                  )}

                  {/* Tag Section - hidden when forCreatorMessaging */}
                  {!forCreatorMessaging && (
                    <div className="caption-tag-section">
                      {(selectedTaggedUsersByFile[index] && selectedTaggedUsersByFile[index].length > 0) ? (
                        <>
                          {selectedTaggedUsersByFile[index].map((user) => (
                            <span key={user.username || user._id || user.id} className="tagged-user-chip">
                              @{user.username}
                              <button
                                className="tagged-user-remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFileTaggedUser(index, user.username);
                                }}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            </span>
                          ))}
                          <button
                            className="caption-add-tag-button"
                            onClick={() => handleOpenTagPopupForFile(index)}
                          >
                            <i className="fa fa-plus" />
                          </button>
                        </>
                      ) : (
                        <button
                          className="caption-tag-button"
                          onClick={() => handleOpenTagPopupForFile(index)}
                        >
                          Tag
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* User Tag Modal */}
      <UserTag
        isOpen={showTagPopup}
        initialSelectedUsers={
          tagPopupTarget?.type === 'bundle'
            ? selectedTaggedUsersBundle
            : tagPopupTarget?.type === 'file' && tagPopupTarget?.index !== undefined
              ? (selectedTaggedUsersByFile[tagPopupTarget.index] || [])
              : []
        }
        onConfirm={handleTagPopupConfirm}
        onClose={handleTagPopupClose}
      />
    </>
  );
};

export default ContentCaptionSection;

